import React, { useEffect, useMemo, useState, memo, useCallback } from "react";
import { ModalRegister } from "./ModalRegister";

/**
 * Petit helper pour concat des classes sans lib.
 */
function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

/**
 * Bouton niveau néon (memo pour éviter les re-renders parasites)
 */
const LevelButton = memo(function LevelButton({ lvl, label, active, used, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(lvl)}
      className={cx(
        "relative inline-flex items-center gap-2",
        "rounded-full px-3 py-2 text-xs font-extrabold",
        "border transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-fuchsia-400/40",
        active
          ? cx(
            "border-fuchsia-400/60",
            "bg-gradient-to-b from-fuchsia-500/20 to-cyan-500/10",
            "text-fuchsia-100",
            "shadow-[0_0_18px_rgba(217,70,239,0.25)]"
          )
          : cx(
            "border-white/10 bg-white/5 text-slate-200",
            "hover:bg-white/10 hover:border-white/20"
          ),
        used && "opacity-75"
      )}
      title={used ? "Indice déjà utilisé" : undefined}
    >
      {/* petit point lumineux à gauche */}
      <span
        className={cx(
          "h-2.5 w-2.5 rounded-full",
          active
            ? "bg-fuchsia-300 shadow-[0_0_10px_rgba(217,70,239,0.7)]"
            : "bg-white/20"
        )}
      />
      <span>{label}</span>

      {/* check used */}
      {used && (
        <span className="ml-1 inline-flex items-center rounded-full bg-emerald-500/15 border border-emerald-400/30 px-1.5 py-0.5 text-[10px] text-emerald-200">
          ✓
        </span>
      )}

      {/* glow overlay très léger quand actif */}
      {active && (
        <span className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-r from-fuchsia-500/10 via-cyan-500/5 to-transparent" />
      )}
    </button>
  );
});

/**
 * Petite puce "compteur" (X/4 utilisés)
 */
const UsedPill = memo(function UsedPill({ usedCount, total }) {
  const pct = total > 0 ? Math.round((usedCount / total) * 100) : 0;

  return (
    <div className="mt-2 flex items-center gap-2">
      <span className="text-[11px] text-slate-300">
        <span className="text-slate-200 font-semibold">{usedCount}</span>/{total} utilisés
      </span>

      {/* mini barre */}
      <div className="flex-1 h-2 rounded-full bg-white/5 border border-white/10 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-fuchsia-400/60 to-cyan-400/50"
          style={{ width: `${pct}%` }}
        />
      </div>

      <span className="text-[10px] text-slate-400 tabular-nums">{pct}%</span>
    </div>
  );
});

/**
 * HintsModalHub
 *
 * UX inchangée :
 * - Choix catégorie (énigme)
 * - Choix niveau (l1/l2/l3) => consomme immédiatement
 * - Réponse => confirmation => consomme "answer"
 * - Used state relu du localStorage à chaque ouverture
 */
function HintsModalHub({
  open,
  onClose,
  title = "Indices",
  categories = [],
  markHintUsedOnce,
  defaultCategoryId,
  usedStorageKey,
}) {
  const safeCategories = useMemo(() => categories.filter(Boolean), [categories]);

  const [selectedCategoryId, setSelectedCategoryId] = useState(
    defaultCategoryId ?? safeCategories?.[0]?.id ?? null
  );

  const [selectedLevel, setSelectedLevel] = useState("home"); // home | l1 | l2 | l3 | answer
  const [answerConfirming, setAnswerConfirming] = useState(false);

  // Réponse révélée par catégorie (UI only)
  const [answerRevealedByCat, setAnswerRevealedByCat] = useState({});
  const [usedKeys, setUsedKeys] = useState(() => new Set());

  const selectedCategory = useMemo(() => {
    return safeCategories.find((c) => c.id === selectedCategoryId) ?? null;
  }, [safeCategories, selectedCategoryId]);

  const hintKeyFor = useCallback((category, level) => {
    if (!category?.hintKeyPrefix) return null;
    return `${category.hintKeyPrefix}:${level}`;
  }, []);

  const readUsedKeysFromStorage = useCallback(() => {
    if (!usedStorageKey) return new Set();
    try {
      const raw = localStorage.getItem(usedStorageKey);
      const arr = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(arr)) return new Set();
      return new Set(arr);
    } catch {
      return new Set();
    }
  }, [usedStorageKey]);

  const consumeHint = useCallback(
    (category, level) => {
      if (!category) return;
      if (level === "answer") return; // consommé à la confirmation
      const hintKey = hintKeyFor(category, level);
      if (!hintKey) return;

      markHintUsedOnce?.({ hintKey, level });

      // UI immédiate (même si le storage est écrit ailleurs)
      setUsedKeys((prev) => {
        const next = new Set(prev);
        next.add(hintKey);
        return next;
      });
    },
    [markHintUsedOnce, hintKeyFor]
  );

  const handleSelectCategory = useCallback((categoryId) => {
    setSelectedCategoryId(categoryId);
    setSelectedLevel("home");
    setAnswerConfirming(false);
  }, []);

  const handleSelectLevel = useCallback(
    (level) => {
      if (!selectedCategory) return;

      if (level === "answer") {
        setSelectedLevel("answer");
        setAnswerConfirming(true);
        return;
      }

      consumeHint(selectedCategory, level);
      setSelectedLevel(level);
      setAnswerConfirming(false);
    },
    [selectedCategory, consumeHint]
  );

  const confirmAnswerReveal = useCallback(() => {
    if (!selectedCategory) return;

    const hintKey = hintKeyFor(selectedCategory, "answer");
    if (hintKey) {
      markHintUsedOnce?.({ hintKey, level: "answer" });

      setUsedKeys((prev) => {
        const next = new Set(prev);
        next.add(hintKey);
        return next;
      });
    }

    setAnswerRevealedByCat((prev) => ({
      ...prev,
      [selectedCategory.id]: true,
    }));

    setAnswerConfirming(false);
    setSelectedLevel("answer");
  }, [selectedCategory, hintKeyFor, markHintUsedOnce]);

  // Reset à l’ouverture + reload usedKeys depuis localStorage
  useEffect(() => {
    if (!open) return;

    const initialCat =
      selectedCategoryId && safeCategories.some((c) => c.id === selectedCategoryId)
        ? selectedCategoryId
        : defaultCategoryId ?? safeCategories?.[0]?.id ?? null;

    setSelectedCategoryId(initialCat);
    setSelectedLevel("home");
    setAnswerConfirming(false);
    setUsedKeys(readUsedKeysFromStorage());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const revealed = selectedCategory ? !!answerRevealedByCat[selectedCategory.id] : false;

  const isLevelUsed = useCallback(
    (level) => {
      if (!selectedCategory) return false;
      const k = hintKeyFor(selectedCategory, level);
      if (!k) return false;
      return usedKeys.has(k);
    },
    [selectedCategory, hintKeyFor, usedKeys]
  );

  const totalLevels = useMemo(() => ["l1", "l2", "l3", "answer"], []);

  return (
    <ModalRegister open={open} title={title} onClose={onClose}>
      {/* ====== WRAPPER "NEON PANEL" ====== */}
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-slate-950/60 to-black/40 p-4 shadow-[0_0_40px_rgba(56,189,248,0.08)]">
        {/* halos décoratifs */}
        <div className="pointer-events-none absolute -top-24 -left-24 h-64 w-64 rounded-full bg-fuchsia-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(217,70,239,0.12),transparent_40%),radial-gradient(circle_at_70%_80%,rgba(34,211,238,0.10),transparent_45%)]" />

        {/* En-tête style scoreboard */}
        <div className="relative flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
              Assistance
            </p>
            <h3 className="mt-1 text-base font-extrabold text-slate-100">
              Centre d’indices

            </h3>
            <p className="mt-1 text-xs text-slate-300">
              Choisissez une énigme, puis un niveau. Chaque niveau consommé vous fait perdre des points.
            </p>
          </div>

          {/* badge total consommés (toutes catégories) */}
          <div className="shrink-0 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 flex items-center flex-col">
            <p className="mt-0.5 text-lg font-extrabold text-slate-100 tabular-nums">
              {usedKeys.size}
            </p>
            <p className="text-[9px] uppercase tracking-[0.2em] text-slate-400">
              Utilisés
            </p>

          </div>
        </div>

        {/* Divider néon */}
        <div className="relative my-4 h-px w-full overflow-hidden rounded">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-fuchsia-400/40 to-transparent" />
        </div>

        {/* ====== CATEGORIES ====== */}
        <div className="relative">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400 mb-2">
            Choisir une énigme
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {safeCategories.map((cat) => {
              const active = cat.id === selectedCategoryId;

              const usedCount = totalLevels.reduce((acc, lvl) => {
                const k = `${cat.hintKeyPrefix}:${lvl}`;
                return acc + (usedKeys.has(k) ? 1 : 0);
              }, 0);

              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleSelectCategory(cat.id)}
                  className={cx(
                    "group relative text-left rounded-2xl p-[1px] transition-all duration-200",
                    active
                      ? "bg-gradient-to-r from-fuchsia-500/50 via-cyan-500/30 to-fuchsia-500/40 shadow-[0_0_18px_rgba(34,211,238,0.12)]"
                      : "bg-white/5 hover:bg-white/10"
                  )}
                >
                  <div
                    className={cx(
                      "rounded-2xl p-3 border transition-all duration-200",
                      active
                        ? "border-white/10 bg-gradient-to-b from-slate-950/70 to-black/50"
                        : "border-white/5 bg-black/20 opacity-80 group-hover:opacity-100 group-hover:bg-black/28"
                    )}

                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={cx("text-sm font-extrabold", active ? "text-fuchsia-100" : "text-slate-100")}>
                          {cat.title}
                        </p>
                        {cat.subtitle && (
                          <p className="mt-1 text-xs text-slate-300">{cat.subtitle}</p>
                        )}
                      </div>

                      {/* Pastille active */}
                      <div
                        className={cx(
                          "h-3.5 w-3.5 rounded-full mt-0.5 shrink-0",
                          active
                            ? "bg-fuchsia-300 shadow-[0_0_14px_rgba(217,70,239,0.65)]"
                            : "bg-white/15"
                        )}
                      />
                    </div>

                    <UsedPill usedCount={usedCount} total={totalLevels.length} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ====== LEVELS ====== */}
        <div className="relative mt-5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                {selectedCategory?.title ? `${selectedCategory.title} — Niveaux d’indice` : "Niveaux d’indice"}
              </p>
              {selectedCategory?.subtitle && (
                <p className="mt-1 text-[11px] text-slate-500 truncate">
                  {selectedCategory.subtitle}
                </p>
              )}
            </div>

          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <LevelButton
              lvl="l1"
              label="Niveau 1"
              active={selectedLevel === "l1"}
              used={isLevelUsed("l1")}
              onSelect={handleSelectLevel}
            />
            <LevelButton
              lvl="l2"
              label="Niveau 2"
              active={selectedLevel === "l2"}
              used={isLevelUsed("l2")}
              onSelect={handleSelectLevel}
            />
            <LevelButton
              lvl="l3"
              label="Niveau 3"
              active={selectedLevel === "l3"}
              used={isLevelUsed("l3")}
              onSelect={handleSelectLevel}
            />
            <LevelButton
              lvl="answer"
              label="Réponse"
              active={selectedLevel === "answer"}
              used={isLevelUsed("answer")}
              onSelect={handleSelectLevel}
            />
          </div>

          {/* ====== CONTENT "TERMINAL" ====== */}
          <div className="relative mt-4 rounded-2xl p-[1px] bg-gradient-to-r from-fuchsia-500/30 via-white/10 to-cyan-500/25">
            <div className="relative rounded-2xl border border-white/10 bg-black/40 p-4 overflow-hidden">
              {/* scanlines léger */}
              <div className="pointer-events-none absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_bottom,rgba(255,255,255,0.12)_1px,transparent_1px)] bg-[length:100%_10px]" />
              {/* coin glow */}
              <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-cyan-400/10 blur-2xl" />
              <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-fuchsia-400/10 blur-2xl" />

              {selectedLevel === "home" && (
                <p className="relative text-sm text-slate-200">
                  Sélectionnez un niveau pour afficher un indice.
                </p>
              )}

              {selectedLevel === "l1" &&
                (selectedCategory?.levels?.l1 ?? (
                  <p className="relative text-sm text-slate-200">
                    Aucun indice de niveau 1 pour cette énigme.
                  </p>
                ))}

              {selectedLevel === "l2" &&
                (selectedCategory?.levels?.l2 ?? (
                  <p className="relative text-sm text-slate-200">
                    Aucun indice de niveau 2 pour cette énigme.
                  </p>
                ))}

              {selectedLevel === "l3" &&
                (selectedCategory?.levels?.l3 ?? (
                  <p className="relative text-sm text-slate-200">
                    Aucun indice de niveau 3 pour cette énigme.
                  </p>
                ))}

              {selectedLevel === "answer" && (
                <>
                  {answerConfirming && !revealed ? (
                    <div className="relative rounded-2xl border border-red-500/30 bg-gradient-to-b from-red-500/15 to-black/20 p-4">
                      <p className="text-sm text-red-100 font-extrabold">
                        Afficher la réponse ?
                      </p>
                      <p className="mt-1 text-xs text-slate-200">
                        Cela vous donnera directement la réponse. Vous perdrez des points.
                        N&apos;utilisez que si vous êtes bloqués.
                      </p>

                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          onClick={confirmAnswerReveal}
                          className={cx(
                            "rounded-xl px-3 py-2 text-xs font-extrabold",
                            "bg-gradient-to-r from-red-600/90 to-fuchsia-600/70",
                            "text-white border border-white/10",
                            "shadow-[0_0_16px_rgba(239,68,68,0.20)]",
                            "hover:brightness-110 transition"
                          )}
                        >
                          Oui, afficher
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setAnswerConfirming(false);
                            setSelectedLevel("l3");
                          }}
                          className={cx(
                            "rounded-xl px-3 py-2 text-xs font-extrabold",
                            "bg-white/5 text-slate-100 border border-white/10",
                            "hover:bg-white/10 transition"
                          )}
                        >
                          Non, revenir
                        </button>
                      </div>
                    </div>
                  ) : revealed ? (
                    selectedCategory?.levels?.answer ?? (
                      <p className="relative text-sm text-slate-200">
                        Aucune réponse définie pour cette énigme.
                      </p>
                    )
                  ) : (
                    <p className="relative text-sm text-slate-200">
                      Cliquez sur “Réponse” puis confirmez pour la révéler.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalRegister>
  );
}

export default memo(HintsModalHub);
